// Glyph barrel — re-exports every glyph component plus a GLYPHS map
// keyed by string identifier. Production code that renders units does
// NOT import individual glyphs; it does GLYPHS[unit.glyph] to look up
// the right component for a given unit's data.
//
// Adding a new glyph:
//   1. Author the SVG in <Name>.tsx following the existing style
//      (paper gradients, dashed crease lines, beady eye)
//   2. Export from this file
//   3. Add an entry to the GLYPHS map below
//   4. Reference by the new key in unit JSON files

import type { ComponentType } from 'react'
import type { GlyphProps } from './shared'

export { Crane } from './Crane'
export { Frog } from './Frog'
export { Fish } from './Fish'
export { Tiger } from './Tiger'
export { Beetle } from './Beetle'
export { Snake } from './Snake'
export { Butterfly } from './Butterfly'
export { Wolf } from './Wolf'
export { Fox } from './Fox'
export { Pup } from './Pup'
export { Owl } from './Owl'
export { Mantis } from './Mantis'
export { Salamander } from './Salamander'
export { Tortoise } from './Tortoise'
export { Mouse } from './Mouse'
export { Phoenix } from './Phoenix'
export { Stag } from './Stag'
export { Moth } from './Moth'
export { Ray } from './Ray'
export { Mite } from './Mite'
export { Tadpole } from './Tadpole'
export { Smelt } from './Smelt'
export { Koi } from './Koi'
export { Flock } from './Flock'
export { Bear } from './Bear'
export { Boar } from './Boar'
export { Wyrm } from './Wyrm'
export { Caiman } from './Caiman'
export { Hawk } from './Hawk'
export { Whale } from './Whale'
export { Serpent } from './Serpent'
export { WalkingStick } from './WalkingStick'
export { Pterodactyl } from './Pterodactyl'
export { TallyHawk } from './TallyHawk'
export { Skink } from './Skink'
export { Swallow } from './Swallow'
export { Quail } from './Quail'
export { Bee } from './Bee'
export { Pig } from './Pig'
export { Goldfish } from './Goldfish'
export { Spider } from './Spider'
export { HeroCrane } from './HeroCrane'
export { TieredCritter } from './TieredCritter'
export { TIERED_GLYPHS } from './tieredGlyphs'

import { Crane } from './Crane'
import { Frog } from './Frog'
import { Fish } from './Fish'
import { Tiger } from './Tiger'
import { Beetle } from './Beetle'
import { Snake } from './Snake'
import { Butterfly } from './Butterfly'
import { Wolf } from './Wolf'
import { Fox } from './Fox'
import { Pup } from './Pup'
import { Owl } from './Owl'
import { Mantis } from './Mantis'
import { Salamander } from './Salamander'
import { Tortoise } from './Tortoise'
import { Mouse } from './Mouse'
import { Phoenix } from './Phoenix'
import { Stag } from './Stag'
import { Moth } from './Moth'
import { Ray } from './Ray'
import { Mite } from './Mite'
import { Tadpole } from './Tadpole'
import { Smelt } from './Smelt'
import { Koi } from './Koi'
import { Flock } from './Flock'
import { Bear } from './Bear'
import { Boar } from './Boar'
import { Wyrm } from './Wyrm'
import { Caiman } from './Caiman'
import { Hawk } from './Hawk'
import { Whale } from './Whale'
import { Serpent } from './Serpent'
import { WalkingStick } from './WalkingStick'
import { Pterodactyl } from './Pterodactyl'
import { TallyHawk } from './TallyHawk'
import { Skink } from './Skink'
import { Swallow } from './Swallow'
import { Quail } from './Quail'
import { Bee } from './Bee'
import { Pig } from './Pig'
import { Goldfish } from './Goldfish'
import { Spider } from './Spider'

export type { GlyphProps }

/**
 * GLYPHS — string → component map. Data-driven rendering looks up the
 * right glyph by string key.
 *
 *   const Glyph = GLYPHS[unit.glyph] ?? GLYPHS.crane
 *   <Glyph size={80} />
 *
 * Keys are lowercase snake_case to match unit data conventions.
 */
// Canonical key type lives in @origami/shared (GlyphKeySchema). Importing
// it here would create a circular concern (the design-system shouldn't
// depend on shared just to type a Record), so the map stays loosely typed
// as Record<string, ...> and the schema enforces validity at the data
// boundary.
export const GLYPHS: Record<string, ComponentType<GlyphProps>> = {
  crane: Crane,
  frog: Frog,
  fish: Fish,
  tiger: Tiger,
  beetle: Beetle,
  snake: Snake,
  butterfly: Butterfly,
  wolf: Wolf,
  fox: Fox,
  pup: Pup,
  owl: Owl,
  mantis: Mantis,
  salamander: Salamander,
  tortoise: Tortoise,
  mouse: Mouse,
  phoenix: Phoenix,
  stag: Stag,
  moth: Moth,
  ray: Ray,
  mite: Mite,
  tadpole: Tadpole,
  smelt: Smelt,
  koi: Koi,
  flock: Flock,
  bear: Bear,
  boar: Boar,
  wyrm: Wyrm,
  caiman: Caiman,
  hawk: Hawk,
  whale: Whale,
  serpent: Serpent,
  walking_stick: WalkingStick,
  pterodactyl: Pterodactyl,
  tally_hawk: TallyHawk,
  skink: Skink,
  swallow: Swallow,
  quail: Quail,
  bee: Bee,
  pig: Pig,
  goldfish: Goldfish,
  spider: Spider,
}
